import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BackgroundJobs, SpecraButton, SpecraDashboardPage, SpecraEyebrow, SpecraPageHeader, SpecraStatusDot, SpecraSurface, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useRecentChats } from '../../components/ChatHistory';
import { useMyNotifications } from '../../components/notifications/hooks';
import { useAuth } from '../../contexts/authContext';
import { startChat } from '../../lib/chat/startChat';
import { selectProcessingJobs, useProcessingJobsStore } from '../../lib/processing/jobsStore';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { formatRelativeTime, greeting } from '../../utils/time';
import { EMPTY_FILTER, summaryBody } from '../Delivery/CommandCenter/filters';
import { usePortfolioSummary } from '../Delivery/CommandCenter/hooks';
import { todayISO } from '../Delivery/Deliverables/lib/format';
import { ccScope, isOwn, parseScope, programsWords } from './areas';
import { assistPrompts } from './assist';
import { GettingStarted } from './GettingStarted';
import { useMyMatchStats, useMyWork, usePursuitPipeline } from './hooks';
import { agendaOf, verdictOf } from './myWork';
import { TodayBand } from './TodayBand';
import { WaitingOnYou } from './WaitingOnYou';
import { HowItIsTrending, YourAreas } from './YourAreas';
// SAD-1005: the Dashboard answers "what is waiting on me today, and is
// anything I am responsible for on fire?" Personal first (the Today band and
// Waiting on You), the portfolio second, everything deep handed off.
/** The rail's recent chats: one bounded read, the latest first. */ const RECENT_CHATS = 3;
/** The rail's On your work rows. */ const ACTIVITY_ROWS = 4;
const plural = (n, one, many = `${one}s`)=>`${n.toLocaleString()} ${n === 1 ? one : many}`;
/** The one-line factual summary: each clause only where its area is visible. */ function SummaryLine({ work, programs, own, onTime, decisionsDue }) {
    if (work.isLoading) return 'Reading what is waiting on you…';
    const lead = work.total === 0 ? /*#__PURE__*/ _jsx("strong", {
        className: "font-medium text-foreground",
        children: "Nothing is waiting on you"
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("strong", {
                className: "font-medium text-foreground",
                children: [
                    plural(work.total, 'thing'),
                    " waiting on you"
                ]
            }),
            work.overdue > 0 ? `, ${work.overdue.toLocaleString()} of them overdue` : ', none overdue'
        ]
    });
    let delivery = '';
    if (programs === 0) delivery = 'programs appear here once someone adds you to one';
    else if (programs && onTime) {
        const whose = own ? programsWords(programs, true) : `the ${plural(programs, 'program')} you can see`;
        delivery = `${whose} ${programs === 1 ? 'is' : 'are'} ${onTime.value}% on time against a ${onTime.target} target`;
    }
    const pursuits = decisionsDue ? `${plural(decisionsDue, 'pursuit decision')} due in the next 14 days` : '';
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            lead,
            delivery ? ` · ${delivery}` : '',
            pursuits ? ` · ${pursuits}` : '',
            "."
        ]
    });
}
function RecentChats() {
    const { chats, isLoading, error, refetch } = useRecentChats(RECENT_CHATS);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-2)]",
        "data-testid": Testids.dashboard.recentChats,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-baseline justify-between",
                children: [
                    /*#__PURE__*/ _jsx(SpecraEyebrow, {
                        children: "Recent chats"
                    }),
                    /*#__PURE__*/ _jsx(Link, {
                        className: "text-[length:var(--fs-ui-meta)] text-brand hover:underline",
                        to: "/history",
                        children: "All →"
                    })
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "Loading…"
            }) : error ? /*#__PURE__*/ _jsx(SpecraButton, {
                className: "text-[length:var(--fs-ui-meta)]",
                onClick: ()=>refetch(),
                size: "inline",
                variant: "link",
                children: "Couldn't load chats. Retry"
            }) : chats.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "Your conversations with Assist appear here, newest first."
            }) : /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-border",
                children: chats.slice(0, RECENT_CHATS).map((chat)=>/*#__PURE__*/ _jsxs(Link, {
                        className: "flex items-baseline justify-between gap-[var(--space-3)] py-[var(--space-2)] hover:text-brand",
                        "data-testid": Testids.dashboard.recentChatItem,
                        to: `/assistant/${chat.chatId}`,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: "min-w-0 truncate",
                                size: "body-ui",
                                children: chat.title
                            }),
                            /*#__PURE__*/ _jsx(SpecraText, {
                                className: "shrink-0",
                                size: "meta",
                                tone: "muted",
                                children: formatRelativeTime(chat.updatedAt)
                            })
                        ]
                    }, chat.chatId))
            })
        ]
    });
}
/** SAD-1005 slice 6: the Assist prompts, grounded in the viewer's own queue. */ function AssistBlock({ prompts }) {
    const navigate = useNavigate();
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-2)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                children: "Assist"
            }),
            /*#__PURE__*/ _jsxs(SpecraSurface, {
                className: "space-y-[var(--space-2)] border p-[var(--space-2-5)]",
                radius: "lg",
                tone: "card",
                children: [
                    /*#__PURE__*/ _jsxs(Link, {
                        className: "flex items-center gap-[var(--space-2-5)] rounded-[var(--radius-md)] px-[var(--space-1-5)] py-[var(--space-1-5)] text-[length:var(--fs-ui-body)] text-muted-foreground transition-colors hover:text-foreground",
                        to: "/assistant",
                        children: [
                            /*#__PURE__*/ _jsx(Sparkles, {
                                className: "size-3.5 shrink-0"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "min-w-0 flex-1",
                                children: "Ask about your day, a program or a pursuit…"
                            })
                        ]
                    }),
                    prompts.map((p)=>/*#__PURE__*/ _jsxs(SpecraSurface, {
                            className: "px-[var(--space-3)] py-[var(--space-2)]",
                            "data-testid": Testids.dashboard.assistPrompt,
                            interactive: true,
                            onClick: ()=>startChat(navigate, p.prompt),
                            radius: "md",
                            tone: "rail",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "div",
                                    size: "body-ui",
                                    children: p.title
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "div",
                                    size: "meta",
                                    tone: "muted",
                                    children: p.sub
                                })
                            ]
                        }, p.id))
                ]
            })
        ]
    });
}
/** SAD-1005 slice 6: On your work, the bell's own read of activity on the viewer's items. */ function OnYourWork({ companyId }) {
    const { data } = useMyNotifications(companyId, {
        shareWithBell: true
    });
    const items = (data?.notifications ?? []).slice(0, ACTIVITY_ROWS);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-2)]",
        "data-testid": Testids.dashboard.activity,
        children: [
            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                children: "On your work"
            }),
            items.length === 0 ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: "Filings, decisions and shares on things you own appear here."
            }) : /*#__PURE__*/ _jsx("div", {
                className: "divide-y divide-border",
                children: items.map((n)=>{
                    const unread = !n.acknowledgedAt;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-[var(--space-2)] py-[var(--space-2)]",
                        "data-testid": Testids.dashboard.activityItem,
                        "data-unread": unread,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "mt-[var(--space-1-5)] w-[var(--space-2)] shrink-0",
                                children: unread ? /*#__PURE__*/ _jsx(SpecraStatusDot, {
                                    tone: "info"
                                }) : null
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "div",
                                        className: "line-clamp-2",
                                        size: "body-ui",
                                        children: n.title
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        as: "div",
                                        size: "meta",
                                        tone: "muted",
                                        children: n.createdAt ? formatRelativeTime(n.createdAt.toDate().toISOString()) : ''
                                    })
                                ]
                            })
                        ]
                    }, n.recipientId);
                })
            }),
            data && data.hiddenCount > 0 ? /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                children: [
                    data.hiddenCount.toLocaleString(),
                    " more on programs you can no longer see."
                ]
            }) : null
        ]
    });
}
function Rail({ prompts, companyId }) {
    const activeJobs = useProcessingJobsStore(selectProcessingJobs);
    const jobs = activeJobs.map((j)=>({
            id: j.batchId,
            name: j.label,
            target: j.kind,
            pct: j.percent
        }));
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-[var(--space-6)] p-[var(--space-6)]",
        children: [
            /*#__PURE__*/ _jsx(AssistBlock, {
                prompts: prompts
            }),
            /*#__PURE__*/ _jsx(RecentChats, {}),
            jobs.length > 0 ? /*#__PURE__*/ _jsx(BackgroundJobs, {
                heading: "Reading now",
                jobs: jobs,
                runningCount: jobs.length
            }) : null,
            /*#__PURE__*/ _jsx(OnYourWork, {
                companyId: companyId
            })
        ]
    });
}
export default function Dashboard() {
    const { user } = useAuth();
    const setShowBreadcrumbs = useLayoutStore((s)=>s.setShowBreadcrumbs);
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowBreadcrumbs(false);
        setShowHeader(false);
        return ()=>{
            setShowBreadcrumbs(true);
            setShowHeader(true);
        };
    }, [
        setShowBreadcrumbs,
        setShowHeader
    ]);
    // The day and the moment the page was read, fixed for the visit.
    const [now] = useState(()=>new Date());
    const today = todayISO(now);
    const work = useMyWork(today);
    // The org-wide band's scope lives in the URL (?scope=mine|all); its one
    // read is the Command Center's summary for the same scope, the same cache entry.
    const [params, setParams] = useSearchParams();
    const scope = parseScope(params);
    const setScope = (next)=>{
        const p = new URLSearchParams(params);
        if (next === 'all') p.set('scope', 'all');
        else p.delete('scope');
        setParams(p, {
            replace: true
        });
    };
    const body = useMemo(()=>summaryBody({
            ...EMPTY_FILTER,
            scope: ccScope(scope)
        }, now), [
        scope,
        now
    ]);
    const summary = usePortfolioSummary(body);
    // SAD-1005 slice 4: the pursuits the viewer can see, in the same scope.
    const pipeline = usePursuitPipeline(scope, today);
    // SAD-1005 slice 5: new matches across the viewer's tracking profiles.
    const matches = useMyMatchStats();
    const verdict = useMemo(()=>verdictOf(work.ranked, today, {
            total: work.total,
            overdue: work.overdue
        }), [
        work,
        today
    ]);
    const prompts = useMemo(()=>assistPrompts(work.ranked, {
            programs: summary.data?.body.programs.length ?? null
        }), [
        work.ranked,
        summary.data
    ]);
    const agenda = useMemo(()=>agendaOf(work.ranked, today, work.agendaDays), [
        work.ranked,
        today,
        work.agendaDays
    ]);
    const programs = summary.isSuccess ? summary.data.body.programs.length : null;
    const onTimeKpi = summary.data?.body.kpis.find((k)=>k.id === 'on_time');
    const onTime = onTimeKpi?.hasValue ? {
        value: onTimeKpi.value,
        target: onTimeKpi.target
    } : null;
    // Nothing waiting, and every read answered (a failed read is not "nothing").
    const nothingWaiting = !work.isLoading && work.failed.length === 0 && work.total === 0;
    // Getting Started only when the page has nothing else to show.
    const gettingStarted = nothingWaiting && programs === 0 && !pipeline.data?.pursuits;
    return /*#__PURE__*/ _jsxs(SpecraDashboardPage, {
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            actions: /*#__PURE__*/ _jsx("div", {
                className: "text-right",
                "data-testid": Testids.dashboard.asOf,
                children: /*#__PURE__*/ _jsxs(SpecraText, {
                    as: "div",
                    size: "meta",
                    tone: "muted",
                    children: [
                        now.toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        }),
                        ' ',
                        "· ",
                        now.toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit'
                        })
                    ]
                })
            }),
            description: /*#__PURE__*/ _jsx("span", {
                "data-testid": Testids.dashboard.summaryLine,
                children: /*#__PURE__*/ _jsx(SummaryLine, {
                    decisionsDue: pipeline.data?.decisionsDue?.hasValue ? pipeline.data.decisionsDue.value : null,
                    onTime: onTime,
                    own: isOwn(scope, summary.data?.body),
                    programs: programs,
                    work: work
                })
            }),
            title: greeting(user?.name),
            titleSize: "home"
        }),
        rail: /*#__PURE__*/ _jsx(Rail, {
            companyId: user?.tenant_id ?? '',
            prompts: prompts
        }),
        children: [
            /*#__PURE__*/ _jsx(TodayBand, {
                agenda: agenda,
                loading: work.isLoading,
                today: today,
                verdict: verdict
            }),
            nothingWaiting ? null : /*#__PURE__*/ _jsx(WaitingOnYou, {
                work: work
            }),
            /*#__PURE__*/ _jsx(YourAreas, {
                failed: summary.isError,
                hiddenCount: summary.data?.hiddenCount ?? 0,
                loading: summary.isLoading,
                onRetry: ()=>void summary.refetch(),
                onScope: setScope,
                scope: scope,
                matches: matches.data,
                pipeline: pipeline.data,
                summary: summary.data?.body
            }),
            /*#__PURE__*/ _jsx(HowItIsTrending, {
                pipeline: pipeline.data,
                scope: scope,
                summary: summary.data?.body
            }),
            gettingStarted ? /*#__PURE__*/ _jsx(GettingStarted, {}) : null
        ]
    });
}
